// src/components/admin/branding/PrimaryColorsPanel.tsx
import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import ColorPicker from './ColorPicker';
import { BrandingThemeOptions } from '../../../contexts/BrandingContext';

interface PrimaryColorsPanelProps {
  themeOptions: BrandingThemeOptions | undefined;
  onColorChange: (path: string[], color: string) => void;
}

const PrimaryColorsPanel: React.FC<PrimaryColorsPanelProps> = ({
  themeOptions,
  onColorChange,
}) => {
  // Helper function to get a nested value from an object using a path array
  const getNestedValue = (obj: any, path: string[]): any => {
    return path.reduce(
      (prev, curr) =>
        prev && prev[curr] !== undefined ? prev[curr] : undefined,
      obj,
    );
  };

  // Define the main color options that should appear on the front panel
  const colorOptions = [
    {
      key: 'primary-main',
      label: 'Primary Color',
      description: 'Used for buttons, links, and active elements',
      path: ['palette', 'primary', 'main'],
      defaultValue: '#F13C00',
    },
    {
      key: 'secondary-main',
      label: 'Secondary Color',
      description: 'Used for less prominent UI elements',
      path: ['palette', 'secondary', 'main'],
      defaultValue: '#1f3473',
    },
    {
      key: 'background-default',
      label: 'Background Color',
      description: 'Main application background (try #121212 for dark theme)',
      path: ['palette', 'background', 'default'],
      defaultValue: '#ffffff',
    },
    {
      key: 'background-paper',
      label: 'Paper/Card Color',
      description:
        'Background for cards, dialogs, and paper elements (try #1e1e1e for dark theme)',
      path: ['palette', 'background', 'paper'],
      defaultValue: '#ffffff',
    },
    {
      key: 'text-primary',
      label: 'Primary Text',
      description: 'Main text color (try #ffffff for dark theme)',
      path: ['palette', 'text', 'primary'],
      defaultValue: '#1A1A1E',
    },
    {
      key: 'text-secondary',
      label: 'Secondary Text',
      description: 'Used for less prominent text (try #A0A0A0 for dark theme)',
      path: ['palette', 'text', 'secondary'],
      defaultValue: '#767678',
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Main Theme Colors
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        These colors have the biggest impact on your application's appearance.
        Background and text colors are especially important for dark themes.
      </Typography>
      <Grid container spacing={3}>
        {colorOptions.map((option) => (
          <Grid
            key={option.key}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <ColorPicker
              color={
                getNestedValue(themeOptions, option.path) || option.defaultValue
              }
              onChange={(color) => onColorChange(option.path, color)}
              label={option.label}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {option.description}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PrimaryColorsPanel;
